import { useEffect, useRef } from 'react';
import localforage from 'localforage';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import { loadReports } from '../reports/Services';

const REPORT_TYPE_STRING = {
    user_activity: 'User Activity',
    event_log: 'Event Log',
    logins_by_website: 'Login Activity',
    password_auditing: 'Password Audit',
};

const loadFromLocalForage = async key => {
    return await localforage.getItem(key);
};

const saveToLocalForage = async (key, data) => {
    await localforage.setItem(key, data);
};

export const useReports = (
    currentUser,
    reportType,
    dateRange,
    groupBy = '',
    search = ''
) => {
    // Cache key: includes user ID and report type only (not filters)
    const reportsKey = `${currentUser.uuid}-/api/partner/reports-${reportType}`;

    // Dependency array for invalidation: when these change, trigger refetch
    const filterDeps = JSON.stringify({
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
        groupBy,
        search,
    });

    const fetcher = async () => {
        return await loadReports(reportType, dateRange, groupBy, search);
    };

    const { enqueueSnackbar } = useSnackbar();
    const errorShown = useRef(false);

    const { data, error, isLoading, isValidating, mutate } = useSWR(
        // Include filterDeps in key to trigger refetch on filter change
        [reportsKey, filterDeps],
        fetcher,
        {
            dedupingInterval: 300000, // 5 minutes
            onLoadingSlow: async () => {
                const cachedData = await loadFromLocalForage(reportsKey);
                if (cachedData) {
                    mutate(cachedData, false);
                }
            },
            loadingTimeout: 2500,
            onSuccess: async data => {
                await saveToLocalForage(reportsKey, data);
            },
            revalidateOnMount: true,
            revalidateIfStale: true, // Background refresh after 5 min
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );

    useEffect(() => {
        if (!error) {
            errorShown.current = false;
            return;
        }
        if (errorShown.current) return;
        errorShown.current = true;
        const reportString = REPORT_TYPE_STRING[reportType] || '';
        enqueueSnackbar(
            `Failed to load ${reportString} data. Please try again.`,
            { variant: 'error' }
        );
    }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = async () => {
        await localforage.removeItem(reportsKey);
        await mutate(undefined, { revalidate: true });
    };

    return {
        data,
        error,
        isLoading: typeof data !== 'undefined' ? false : isLoading,
        isValidating,
        mutate,
        refresh,
    };
};
